import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import LeftMenu from "../../components/left-menu";
import axios from "axios";

function toISODate(dt) {
  return dt
    ? new Date(dt).toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" }) // YYYY-MM-DD
    : "";
}

export default function MarketingReview() {
  const [platform, setPlatform] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [result, setResult] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [costs, setCosts] = useState({});
  const [selectedAd, setSelectedAd] = useState(null);
  const [cleanupStatus, setCleanupStatus] = useState("");

  const limit = 40;

  const fetchData = async (reset = false) => {
    if (reset) {
      setResult([]);
      setSkip(0);
    }
    setLoading(true);
    setError("");
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/marketingReview`,
        {
          params: {
            platform,
            startDate: toISODate(startDate),
            endDate: toISODate(endDate),
            skip: reset ? 0 : skip,
            limit,
          },
        }
      );
      const data = resp.data || [];
      setResult((prev) => (reset ? data : [...prev, ...data]));
      setSkip((prev) => (reset ? data.length : prev + data.length));
      setHasMore(data.length === limit);
    } catch (e) {
      setError("Failed to fetch data");
    }
    setLoading(false);
  };

  // Cleanup orphan tickets via backend
  const handleCleanup = async () => {
    setCleanupStatus("");
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/cleanup-orphan-tickets`
      );
      setCleanupStatus(data.message || "Cleanup completed");
    } catch {
      setCleanupStatus("Cleanup failed");
    }
  };

  const handleSearch = () => {
    setSelectedAd(null);
    fetchData(true);
  };
  const handleLoadMore = () => {
    if (!loading && hasMore) fetchData(false);
  };
  const handleCostChange = (adCode, val) => {
    setCosts((prev) => ({ ...prev, [adCode]: val.replace(/\D/g, "") }));
  };

  // CAC = cost / (inquiryCount * conversionRate)
  const calcCAC = (row) => {
    const cost = parseFloat(costs[row.adCode] || 0);
    const cnt = row.inquiryCount || 0;
    const cr = row.salesConversionRate || 0;
    if (!cost || !cnt || !cr) return "-";
    return (cost / (cnt * cr)).toLocaleString();
  };

  const fmtScore = (n) =>
    typeof n === "number" ? n.toFixed(1) : n == null ? "-" : String(n);
  const fmtPct = (n) =>
    typeof n === "number" ? `${(n * 100).toFixed(1)}%` : "-";

  const handleRowClick = async (row) => {
    setSelectedAd({ loading: true });
    try {
      const isUnknown =
        typeof row.adCode === "string" &&
        row.adCode.toLowerCase().startsWith("unknown");
      const codeParam = isUnknown ? undefined : row.adCode;

      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/adDetails`,
        {
          params: {
            adCode: codeParam,
            startDate: toISODate(startDate),
            endDate: toISODate(endDate),
          },
        }
      );
      setSelectedAd(data);
    } catch {
      setSelectedAd({ error: "Failed to load details" });
    }
  };

  useEffect(() => {}, []);

  return (
    <Grid container>
      <Grid item sm={3} md={2}>
        <LeftMenu />
      </Grid>
      <Grid item xs={12} sm={9} md={7} sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Marketing Review
        </Typography>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={platform}
                  label="Platform"
                  onChange={(e) => setPlatform(e.target.value)}
                >
                  <MenuItem value="Google Search Ads">
                    Google Search Ads
                  </MenuItem>
                  <MenuItem value="Meta Ads">Meta Ads</MenuItem>
                  <MenuItem value="Google Display Video Ads">
                    Google Display Video Ads
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={handleCleanup}
                disabled={loading}
              >
                Cleanup Orphans
              </Button>
            </Grid>
          </Grid>
          {error && (
            <Typography color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
          {cleanupStatus && (
            <Typography sx={{ mt: 1 }}>{cleanupStatus}</Typography>
          )}
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>AdCode</TableCell>
                <TableCell>Cost (Rp)</TableCell>
                <TableCell align="right">Inquiry</TableCell>
                <TableCell align="right">Avg Score</TableCell>
                <TableCell align="right">Spam Rate</TableCell>
                <TableCell align="right">PI Rate</TableCell>
                <TableCell align="right">Conv. Rate</TableCell>
                <TableCell align="right">CAC</TableCell>
                <TableCell align="right">Qualified</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.map((row) => (
                <TableRow
                  key={row.adCode}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(row)}
                >
                  <TableCell>{row.adCode}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={costs[row.adCode] || ""}
                      onChange={(e) =>
                        handleCostChange(row.adCode, e.target.value)
                      }
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {row.inquiryCount?.toLocaleString?.() ??
                      row.inquiryCount ??
                      "-"}
                  </TableCell>
                  <TableCell align="right">
                    {fmtScore(row.inqScoreAvg)}
                  </TableCell>
                  <TableCell align="right">
                    {fmtPct(row.spamRate)}
                  </TableCell>
                  <TableCell align="right">{fmtPct(row.piRate)}</TableCell>
                  <TableCell align="right">
                    {fmtPct(row.salesConversionRate)}
                  </TableCell>
                  <TableCell align="right">{calcCAC(row)}</TableCell>
                  <TableCell align="right">
                    {row.qualifiedCount?.toLocaleString?.() ??
                      row.qualifiedCount ??
                      "-"}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && result.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    No data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {hasMore && (
          <Box textAlign="center" my={2}>
            <Button onClick={handleLoadMore} disabled={loading}>
              {loading ? "Loading..." : "Load More"}
            </Button>
          </Box>
        )}
      </Grid>

      <Grid item xs={12} md={3} sx={{ p: 2, borderLeft: "1px solid #eee" }}>
        <Typography variant="h6" gutterBottom>
          Ad Details
        </Typography>
        {!selectedAd && (
          <Typography color="text.secondary">
            Click an ad row to view details.
          </Typography>
        )}
        {selectedAd?.loading && <Typography>Loading...</Typography>}
        {selectedAd?.error && (
          <Typography color="error">{selectedAd.error}</Typography>
        )}
        {selectedAd && !selectedAd.loading && !selectedAd.error && (
          <Box>
            <Typography>
              <b>AdCode:</b> {selectedAd.adCode}
            </Typography>
            {selectedAd.platform && (
              <Typography>
                <b>Platform:</b> {selectedAd.platform}
              </Typography>
            )}

            {selectedAd.stats && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2">
                  Inquiry:{" "}
                  {selectedAd.stats.inquiryCount?.toLocaleString?.() ??
                    selectedAd.stats.inquiryCount ??
                    "-"}
                </Typography>
                <Typography variant="body2">
                  Spam Rate: {fmtPct(selectedAd.stats.spamRate)}
                </Typography>
                <Typography variant="body2">
                  PI Rate: {fmtPct(selectedAd.stats.piRate)}
                </Typography>
                <Typography variant="body2">
                  Conv. Rate: {fmtPct(selectedAd.stats.salesConversionRate)}
                </Typography>
                <Typography variant="body2">
                  Avg Score: {fmtScore(selectedAd.stats.avgScore)}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            {selectedAd.mediaUrl ? (
              selectedAd.contentType &&
              selectedAd.contentType.startsWith("video/") ? (
                <video
                  src={selectedAd.mediaUrl}
                  controls
                  style={{ maxWidth: "100%" }}
                />
              ) : (
                <img
                  src={selectedAd.mediaUrl}
                  alt="Ad Media"
                  style={{ maxWidth: "100%" }}
                />
              )
            ) : (
              <Typography color="text.secondary">(No media)</Typography>
            )}

            {selectedAd.inquiries && selectedAd.inquiries.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Inquiries ({selectedAd.inquiries.length})
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nama</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell align="right">Score</TableCell>
                      <TableCell align="center">PI</TableCell>
                      <TableCell align="center">Paid</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedAd.inquiries.map((inq) => (
                      <TableRow key={inq.inquiryId}>
                        <TableCell>{inq.Nama}</TableCell>
                        <TableCell>{inq.Phone}</TableCell>
                        <TableCell align="right">
                          {fmtScore(inq.inqScore)}
                        </TableCell>
                        <TableCell align="center">
                          {inq.isPI ? "Yes" : "-"}
                        </TableCell>
                        <TableCell align="center">
                          {inq.isConv ? "Yes" : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
